<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">{{ $t("Polls") }}</h5>
          <small class="text-muted">{{
            $t("Manage poll records and history details")
          }}</small>
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">{{ $t("Show") }}:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }} <strong>{{ displayedRange.end }}</strong>
            {{ $t("of") }}
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="onPageChange"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div
            class="filter-fields-group filter-fields-group--cols-3 d-flex align-items-center"
          >
            <v-select
              v-model="selectedClass"
              :options="classes"
              :clearable="true"
              :reduce="(val) => val.id"
              label="name"
              :placeholder="$t('Select ' + getLabel('class'))"
              append-to-body
              class="filter-control filter-field-item"
              @input="onClassFilterChange"
              :disabled="clsLoading"
            />
            <v-select
              v-model="selectedSection"
              :options="sections"
              :clearable="true"
              :reduce="(val) => val.id"
              label="section"
              :placeholder="$t('Select ' + getLabel('section'))"
              :disabled="!selectedClass"
              append-to-body
              class="filter-control filter-field-item"
              @input="onFilterChange"
            />
            <v-select
              v-model="selectedAgeCategory"
              :options="ageOptions"
              :clearable="true"
              :reduce="(val) => val.value"
              label="text"
              :placeholder="$t('Select Age Category')"
              append-to-body
              class="filter-control filter-field-item"
              @input="onFilterChange"
            />
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openAddPollModal"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>{{ $t("Add Poll") }}</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="polls"
          :fields="fields"
          :busy="dataLoading"
          :responsive="false"
          hover
          small
          show-empty
        >
          <template #empty>
            <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
              {{ $t("No records found") }}
            </h3>
          </template>

          <template #table-busy>
            <tbody>
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <td
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + index"
                  class="skeleton-cell"
                >
                  <div class="skeleton-loader"></div>
                </td>
              </tr>
            </tbody>
          </template>

          <template #head()="data">
            <span>{{ $t(getLabel(data.label)) }}</span>
          </template>

          <template #cell(sno)="{ index }">
            {{ (currentPage - 1) * perPage + index + 1 }}
          </template>

          <template #cell(title)="{ item }">
            <span class="table-title-text" :title="item.title">{{
              item.title || "—"
            }}</span>
          </template>

          <template #cell(startDate)="{ item }">
            <span class="table-date-text">{{
              formatDate(item.startDate)
            }}</span>
          </template>

          <template #cell(endDate)="{ item }">
            <span class="table-date-text">{{ formatDate(item.endDate) }}</span>
          </template>

          <template #cell(status)="{ item }">
            <b-badge
              :variant="
                item.status === 'active' ? 'light-success' : 'light-danger'
              "
              class="text-capitalize"
            >
              {{ item.status }}
            </b-badge>
          </template>

          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex justify-content-center">
              <b-button
                v-if="rights.view"
                variant="outline-primary"
                v-b-tooltip.hover.top
                :title="$t('View Responses')"
                class="action-btn-outline mr-1"
                :disabled="pollLoading"
                @click="viewPoll(item)"
              >
                <feather-icon icon="EyeIcon" />
              </b-button>
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                :title="$t('Edit')"
                class="action-btn-table mr-1"
                :disabled="pollLoading"
                @click="editPoll(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add/Edit Poll Modal -->
    <b-modal
      v-model="showPollModal"
      :title="isEdit ? $t('Edit Poll') : $t('Add Poll')"
      centered
      size="lg"
      no-close-on-backdrop
      @hidden="resetPollForm"
    >
      <b-form @submit.prevent>
        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="FileTextIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Poll Details") }}</h6>
          </div>

          <b-row>
            <b-col cols="12" class="mb-1">
              <b-form-group :label="$t('Title') + ' *'">
                <b-form-input
                  v-model="pollForm.poll.title"
                  :placeholder="$t('Enter poll title')"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" class="mb-1">
              <b-form-group :label="$t('Description')">
                <b-form-textarea
                  v-model="pollForm.poll.description"
                  :placeholder="$t('Enter poll description')"
                  rows="3"
                />
              </b-form-group>
            </b-col>

            <b-col md="6" cols="12" class="mb-1">
              <b-form-group :label="$t('Select ' + getLabel('Class'))">
                <v-select
                  v-model="selectedModalClasses"
                  multiple
                  :closeOnSelect="false"
                  :options="[
                    { id: 0, name: $t('All ' + getLabel('Classes')) },
                    ...classes,
                  ]"
                  :reduce="(val) => val.id"
                  label="name"
                  :placeholder="$t('Select ' + getLabel('class'))"
                  @input="onModalClassChange"
                />
              </b-form-group>
            </b-col>

            <b-col md="6" cols="12" class="mb-1">
              <b-form-group :label="$t('Select ' + getLabel('Section'))">
                <v-select
                  v-model="selectedModalSections"
                  multiple
                  :closeOnSelect="false"
                  :options="[
                    { id: 0, section: $t('All ' + getLabel('Sections')) },
                    ...sectionsOptions,
                  ]"
                  :reduce="(val) => val.id"
                  label="section"
                  :placeholder="$t('Select ' + getLabel('section'))"
                  :disabled="selectedModalClasses.length !== 1"
                  @input="onModalSectionChange"
                />
              </b-form-group>
            </b-col>

            <b-col md="6" cols="12" class="mb-1">
              <b-form-group :label="$t('Age Category')">
                <v-select
                  v-model="pollForm.poll.ageCategory"
                  :options="ageOptions"
                  :reduce="(val) => val.value"
                  label="text"
                  :placeholder="$t('Select Age Category')"
                />
              </b-form-group>
            </b-col>

            <b-col md="6" cols="12" class="mb-1 d-flex align-items-end">
              <b-form-checkbox v-model="pollForm.poll.allowMultiple" switch>
                {{ $t("Allow Multiple Answers") }}
              </b-form-checkbox>
            </b-col>

            <b-col md="6" cols="12" class="mb-1">
              <b-form-group :label="$t('Start Date') + ' *'">
                <flat-pickr
                  v-model="pollForm.poll.startDate"
                  :config="flatPickrConfig"
                  class="form-control poll-date-input"
                  :placeholder="$t('Select Start Date')"
                />
              </b-form-group>
            </b-col>

            <b-col md="6" cols="12" class="mb-1">
              <b-form-group :label="$t('End Date') + ' *'">
                <flat-pickr
                  v-model="pollForm.poll.endDate"
                  :config="flatPickrConfig"
                  class="form-control poll-date-input"
                  :placeholder="$t('Select End Date')"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <div class="leave-details-section leave-details-section--items mb-0">
          <div class="section-header">
            <feather-icon icon="ListIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Poll Options") }} *</h6>
          </div>

          <div
            v-for="(opt, index) in pollForm.options"
            :key="index"
            class="poll-option-row d-flex align-items-center mb-1"
          >
            <span class="poll-option-index">{{ index + 1 }}</span>
            <b-form-input
              v-model="opt.optionText"
              :placeholder="$t('Option') + ' ' + (index + 1)"
              class="mr-1"
            />
            <b-button
              v-if="opt.id == 0 && pollForm.options.length > 2"
              variant="flat-danger"
              class="btn-icon p-0 poll-option-remove-btn"
              v-b-tooltip.hover.top
              :title="$t('Remove')"
              @click="removeOption(index)"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>

          <b-button variant="outline-primary" size="sm" @click="addOption">
            <feather-icon icon="PlusIcon" class="mr-50" />
            {{ $t("Add Option") }}
          </b-button>
        </div>
      </b-form>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="showPollModal = false"
          :disabled="saving"
        >
          {{ $t("Cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving || !isFormValid"
          @click="savePoll"
        >
          <b-spinner v-if="saving" small class="mr-50" />
          {{ isEdit ? $t("Update") : $t("Save") }}
        </b-button>
      </template>
    </b-modal>

    <!-- View Poll Results Modal -->
    <b-modal
      v-model="showViewModal"
      :title="$t('Poll Responses')"
      centered
      size="lg"
      @hidden="resetViewData"
    >
      <div v-if="viewLoading" class="text-center py-4">
        <b-spinner variant="primary" />
        <p class="text-muted mb-0 mt-1">{{ $t("Loading") }}...</p>
      </div>

      <template v-else-if="viewPollData">
        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="BarChart2Icon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Poll Details") }}</h6>
          </div>

          <div class="details-meta-grid">
            <div class="details-meta-item details-meta-item--wide">
              <span class="details-label">{{ $t("Title") }}</span>
              <span class="details-value details-value-primary">{{
                viewPollData.title || "—"
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Status") }}</span>
              <span
                class="details-value details-value-success text-capitalize"
                >{{ viewPollData.status || "—" }}</span
              >
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Start Date") }}</span>
              <span class="details-value">{{
                formatDate(viewPollData.startDate)
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("End Date") }}</span>
              <span class="details-value">{{
                formatDate(viewPollData.endDate)
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Responses") }}</span>
              <span class="details-value details-value-info">{{
                viewSelections.length
              }}</span>
            </div>
          </div>

          <div v-if="viewPollData.description" class="details-note-item mt-2">
            <span class="details-label">{{ $t("Description") }}</span>
            <div class="details-note-content">
              {{ viewPollData.description }}
            </div>
          </div>
        </div>

        <div class="leave-details-section leave-details-section--items mb-0">
          <div class="section-header">
            <feather-icon icon="UsersIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Responses") }}</h6>
          </div>

          <div
            v-if="!viewSelections.length"
            class="text-muted text-center py-3"
          >
            {{ $t("No responses found") }}
          </div>
          <div v-else class="poll-items-table-wrap">
            <b-table
              :items="viewSelections"
              :fields="viewFields"
              hover
              small
              bordered
              :responsive="false"
              class="mb-0 poll-items-table"
            >
              <template #head()="data">
                <span>{{ $t(getLabel(data.label)) }}</span>
              </template>

              <template #cell(studentName)="{ item }">
                <span class="font-weight-bold">{{
                  item.studentName || "—"
                }}</span>
              </template>

              <template #cell(className)="{ item }">
                <b-badge variant="light-primary">{{
                  item.className || "—"
                }}</b-badge>
              </template>

              <template #cell(sectionName)="{ item }">
                <b-badge variant="light-info">{{
                  item.sectionName || "—"
                }}</b-badge>
              </template>

              <template #cell(optionText)="{ item }">
                <div
                  v-for="(ansText, ansInd) in item.answers"
                  :key="ansInd"
                  class="poll-answer-chip"
                >
                  {{ ansText }}
                </div>
              </template>

              <template #cell(date)="{ item }">
                <span class="table-date-text">{{ formatDate(item.date) }}</span>
              </template>
            </b-table>
          </div>
        </div>
      </template>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showViewModal = false">
          {{ $t("Close") }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BBadge,
  BPagination,
  VBTooltip,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapActions } from "vuex";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Ripple from "vue-ripple-directive";

export default {
  name: "PollList",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BPagination,
    vSelect,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BSpinner,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      rights: {},
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      polls: [],
      dataLoading: false,
      pollLoading: false,
      currentPage: 1,
      perPage: 50,
      totalCount: 0,
      totalPages: 1,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "30", value: 30 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      fields: [
        {
          key: "sno",
          label: "S.No",
          thClass: "text-center",
          tdClass: "text-center",
        },
        {
          key: "title",
          label: "Title",
          thClass: "col-title",
          tdClass: "col-title",
        },
        { key: "startDate", label: "Start Date", sortable: false },
        { key: "endDate", label: "End Date", sortable: false },
        { key: "status", label: "Status", sortable: false },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
        },
      ],
      classesData: [],
      classes: [],
      sections: [],
      clsLoading: false,
      selectedClass: null,
      selectedSection: null,
      selectedAgeCategory: null,
      ageOptions: [
        // { text: "All", value: "" },
        // { text: "Junior", value: "junior" },
        // { text: "Senior", value: "senior" },
      ],
      showPollModal: false,
      isEdit: false,
      saving: false,
      selectedModalClasses: [0],
      selectedModalSections: [0],
      sectionsOptions: [],
      showViewModal: false,
      viewLoading: false,
      viewPollData: null,
      viewSelections: [],
      viewFields: [
        { key: "studentName", label: "Student Name" },
        // { key: "grNo", label: "GR No" },
        { key: "className", label: "Class" },
        { key: "sectionName", label: "Section" },
        { key: "optionText", label: "Selected Option" },
        { key: "date", label: "Answer Date" },
      ],
      flatPickrConfig: {
        altFormat: "j M, Y h:i K",
        altInput: true,
        enableTime: true,
        dateFormat: "Y-m-d\\TH:i:S",
      },
      pollForm: {
        poll: {
          id: 0,
          title: "",
          description: "",
          classIDs: "",
          secIDs: "",
          ageCategory: "",
          allowMultiple: false,
          startDate: "",
          endDate: "",
          status: "active",
          meta_class: "",
          meta_section: "",
          campusID: null,
        },
        options: [
          { id: 0, pollID: 0, optionText: "" },
          { id: 0, pollID: 0, optionText: "" },
        ],
      },
    };
  },
  computed: {
    isHappiness() {
      return this.reportDomain == "myskoolhappinessfoundation";
    },
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    isFormValid() {
      const p = this.pollForm.poll;
      const validOptions = this.pollForm.options.filter(
        (o) => o.optionText && o.optionText.trim() !== ""
      );
      return (
        p.title &&
        p.title.trim().length > 0 &&
        p.startDate &&
        p.endDate &&
        validOptions.length >= 2 &&
        this.pollForm.options.every(
          (o) => o.optionText && o.optionText.trim().length > 0
        )
      );
    },
  },
  created() {
    // if (!this.$store.state.rights[this.$route.name]) {
    //   this.$router.replace({ name: "misc-not-authorized" });
    //   return;
    // }
    // this.rights = this.$store.state.rights[this.$route.name];
    // if (!this.rights.view) {
    //   this.$router.replace({ name: "misc-not-authorized" });
    //   return;
    // } 
    this.rights = {view: true, add:true, edit: true, delete: true}

    this.loadClasses();
    this.loadPolls();
    if (this.isHappiness) this.loadHappinessDropdowns();
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    getLabel(key) {
      const isHappiness = this.isHappiness;
      const map = {
        class: isHappiness ? "group" : "class",
        classes: isHappiness ? "groups" : "classes",
        section: isHappiness ? "level" : "section",
        sections: isHappiness ? "levels" : "sections",
      };
      const result = map[key.toLowerCase()] ?? key.toLowerCase();
      const isCapitalized =
        key[0] === key[0].toUpperCase() && key[0] !== key[0].toLowerCase();
      return isCapitalized
        ? result.charAt(0).toUpperCase() + result.slice(1)
        : result;
    },

    async loadHappinessDropdowns() {
      const [item1] = await Promise.all([this.loadDropdownList("ageCategory")]);
      this.ageOptions = this.mapDropdownToOptions(item1) || [];
      this.ageOptions.unshift({ text: "All", value: "" });
    },
    async loadDropdownList(category) {
      var obj = {
        url:
          this.$store.state.domain +
          "DropdownList/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&category=" +
          category,
        token: this.$store.state.userData.token,
      };
      const res = await this.get(obj);
      return Array.isArray(res) ? res : [];
    },
    mapDropdownToOptions(items) {
      if (!Array.isArray(items)) return [];
      return items
        .filter((el) => el.title != null && el.title !== "")
        .map((item) => ({
          text: item.title,
          value: item.title,
          id: item.id,
        }));
    },

    async loadClasses() {
      this.clsLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const data =
          response && response.data !== undefined ? response.data : response;
        this.classesData = Array.isArray(data) ? data : [];
        this.classes = this.classesData
          .map((el) => el.cls)
          .filter((cls) => cls && cls.id);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
        this.classesData = [];
      } finally {
        this.clsLoading = false;
      }
    },

    loadSectionsFromClass(classId) {
      if (!classId) {
        this.sections = [];
        return;
      }
      const selectedClassData = this.classesData.find(
        (item) => item.cls && item.cls.id === classId
      );
      if (selectedClassData && selectedClassData.sections) {
        this.sections = selectedClassData.sections.map((sec) => ({
          id: sec.id,
          section: sec.section || sec.name || `Section ${sec.id}`,
        }));
      } else {
        this.sections = [];
      }
    },

    onClassFilterChange() {
      this.selectedSection = null;
      this.sections = [];
      if (this.selectedClass) {
        this.loadSectionsFromClass(this.selectedClass);
      }
      this.currentPage = 1;
      this.loadPolls();
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadPolls();
    },

    buildLoadUrl() {
      let url =
        this.$store.state.domain +
        "Poll/LoadPollsAdmin?db=" +
        this.$store.state.userData.db +
        "&cID=" +
        this.$store.state.userData.cId +
        "&page=" +
        this.currentPage +
        "&pageSize=" +
        this.perPage;

      if (this.selectedClass != null) {
        url += "&classID=" + this.selectedClass;
      }
      if (this.selectedSection != null) {
        url += "&sectionID=" + this.selectedSection;
      }
      if (this.selectedAgeCategory != null && this.selectedAgeCategory !== "") {
        url += "&ageCategory=" + encodeURIComponent(this.selectedAgeCategory);
      }

      return url;
    },

    async loadPolls() {
      this.dataLoading = true;
      try {
        const obj = {
          url: this.buildLoadUrl(),
          token: this.$store.state.userData.token,
          returnData: true,
        };
        const response = await this.get(obj);
        if (response === false) {
          this.polls = [];
          this.totalCount = 0;
          this.$bvToast.toast("Failed to load polls.", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
          return;
        }
        const payload =
          response && typeof response === "object" ? response : {};
        const container = payload.data !== undefined ? payload.data : payload;
        let records = [];
        if (Array.isArray(container?.data)) {
          records = container.data;
        } else if (Array.isArray(container)) {
          records = container;
        } else if (Array.isArray(payload)) {
          records = payload;
        } else if (Array.isArray(payload.data)) {
          records = payload.data;
        }

        const meta = {
          totalCount:
            payload?.totalCount ??
            container?.totalCount ??
            payload?.data?.totalCount,
          page: payload?.page ?? container?.page,
          pageSize: payload?.pageSize ?? container?.pageSize,
          totalPages: payload?.totalPages ?? container?.totalPages,
        };

        this.polls = records || [];
        const pageSizeFromApi = Number(meta.pageSize) || this.perPage;
        const totalFromApi = Number(meta.totalCount);
        const totalRecords =
          !Number.isNaN(totalFromApi) && totalFromApi >= 0
            ? totalFromApi
            : records.length;
        this.totalCount = totalRecords;
        if (!Number.isNaN(Number(meta.page)) && Number(meta.page) > 0) {
          this.currentPage = Number(meta.page);
        }
        this.perPage = pageSizeFromApi;
        this.totalPages =
          Number(meta.totalPages) ||
          Math.max(1, Math.ceil(this.totalCount / (this.perPage || 1)));
      } catch (e) {
        console.error("Load polls error:", e);
        this.polls = [];
        this.totalCount = 0;
        this.$bvToast.toast("Failed to load polls.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
      }
    },

    async editPoll(item) {
      if (!this.rights.edit) return;
      this.pollLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Poll/LoadPoll?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&pollID=" +
            item.id,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        if (response) {
          this.isEdit = true;

          const pollData = response.poll || {};
          this.pollForm.poll = {
            id: pollData.id || 0,
            title: pollData.title || "",
            description: pollData.description || "",
            classIDs: pollData.classIDs || "",
            secIDs: pollData.secIDs || "",
            ageCategory: pollData.ageCategory || "",
            allowMultiple: !!pollData.allowMultiple,
            startDate: pollData.startDate || null,
            endDate: pollData.endDate || null,
            status: pollData.status || "active",
            meta_class: "",
            meta_section: "",
            campusID: this.$store.state.userData.cId,
          };
          if (pollData.classIDs) {
            this.selectedModalClasses = pollData.classIDs
              .split(",")
              .map(Number);
          } else {
            this.selectedModalClasses = [0];
          }

          if (pollData.secIDs) {
            this.selectedModalSections = pollData.secIDs.split(",").map(Number);
          } else {
            this.selectedModalSections = [0];
          }

          this.onModalClassChange();

          const optionsList = response.options || [];
          this.pollForm.options = optionsList.map((o) => {
            const opt = o.option || o;
            return {
              id: opt.id || 0,
              pollID: opt.pollID || pollData.id,
              optionText: opt.optionText || "",
            };
          });

          while (this.pollForm.options.length < 2) {
            this.pollForm.options.push({
              id: 0,
              pollID: pollData.id || 0,
              optionText: "",
            });
          }

          this.showPollModal = true;
        } else {
          this.$bvToast.toast("Failed to load poll details.", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        }
      } catch (error) {
        console.error("Error loading poll:", error);
        this.$bvToast.toast("Failed to load poll details.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.pollLoading = false;
      }
    },

    openAddPollModal() {
      if (!this.rights.add) return;
      this.isEdit = false;
      this.selectedModalClasses = [0];
      this.selectedModalSections = [0];
      this.sectionsOptions = [];
      this.pollForm = {
        poll: {
          id: 0,
          title: "",
          description: "",
          classIDs: "",
          secIDs: "",
          ageCategory: "",
          allowMultiple: false,
          startDate: "",
          endDate: "",
          status: "active",
          meta_class: "",
          meta_section: "",
          campusID: this.$store.state.userData.cId,
        },
        options: [
          { id: 0, pollID: 0, optionText: "" },
          { id: 0, pollID: 0, optionText: "" },
        ],
      };
      this.showPollModal = true;
    },

    addOption() {
      this.pollForm.options.push({
        id: 0,
        pollID: this.pollForm.poll.id || 0,
        optionText: "",
      });
    },

    removeOption(index) {
      if (this.pollForm.options.length > 2) {
        this.pollForm.options.splice(index, 1);
      }
    },

    onModalClassChange() {
      this.sectionsOptions = [];
      if (this.selectedModalClasses.at(-1) === 0) {
        this.selectedModalClasses = [0];
        this.selectedModalSections = [0];
      } else {
        this.selectedModalClasses = this.selectedModalClasses.filter(
          (el) => el !== 0
        );
        if (this.selectedModalClasses.length === 1) {
          this.selectedModalSections = [0];
          const classObj = this.classesData.find(
            (c) => c.cls && c.cls.id === this.selectedModalClasses[0]
          );
          if (classObj && classObj.sections) {
            this.sectionsOptions = classObj.sections.map((sec) => ({
              id: sec.id,
              section: sec.section || sec.name || `Section ${sec.id}`,
            }));
          }
        } else {
          this.selectedModalSections = [0];
        }
      }
    },

    onModalSectionChange() {
      if (this.selectedModalSections.at(-1) === 0) {
        this.selectedModalSections = [0];
      } else {
        this.selectedModalSections = this.selectedModalSections.filter(
          (el) => el !== 0
        );
      }
    },

    resetPollForm() {
      this.isEdit = false;
      this.saving = false;
      this.selectedModalClasses = [0];
      this.selectedModalSections = [0];
      this.sectionsOptions = [];
    },

    async savePoll() {
      if (this.isEdit ? !this.rights.edit : !this.rights.add) return;
      if (!this.isFormValid) return;
      if (
        new Date(this.pollForm.poll.startDate) >=
        new Date(this.pollForm.poll.endDate)
      ) {
        this.$bvToast.toast("End date must be after start date.", {
          title: "Validation Error",
          variant: "warning",
          solid: true,
        });
        return;
      }
      this.saving = true;
      try {
        const payload = {
          poll: {
            id: this.pollForm.poll.id || 0,
            title: this.pollForm.poll.title,
            description: this.pollForm.poll.description || "",
            classIDs: this.selectedModalClasses.includes(0)
              ? ""
              : this.selectedModalClasses.join(","),
            secIDs: this.selectedModalSections.includes(0)
              ? ""
              : this.selectedModalSections.join(","),
            ageCategory: this.pollForm.poll.ageCategory || "",
            allowMultiple: this.pollForm.poll.allowMultiple || false,
            startDate: this.pollForm.poll.startDate,
            endDate: this.pollForm.poll.endDate,
            status: this.pollForm.poll.status || "active",
            meta_class: "",
            meta_section: "",
            campusID: this.$store.state.userData.cId,
          },
          options: this.pollForm.options.map((o) => ({
            id: o.id || 0,
            pollID: this.pollForm.poll.id || 0,
            optionText: o.optionText,
          })),
        };

        const url =
          this.$store.state.domain +
          "Poll/SavePoll?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&userID=" +
          this.$store.state.userData.userID;

        const obj = {
          url: url,
          body: payload,
          message: this.isEdit
            ? "Poll updated successfully."
            : "Poll created successfully.",
          token: this.$store.state.userData.token,
          context: this,
        };

        const response = await this.post(obj);
        if (response) {
          this.showPollModal = false;
          this.loadPolls();
        }
      } catch (error) {
        console.error("Error saving poll:", error);
        this.$bvToast.toast("Failed to save poll.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.saving = false;
      }
    },

    async viewPoll(item) {
      if (!this.rights.view) return;
      this.viewLoading = true;
      this.viewPollData = null;
      this.viewSelections = [];
      this.showViewModal = true;
      this.pollLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Poll/LoadAdminAnswers?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&pollID=" +
            item.id,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        if (response) {
          this.viewPollData = response.poll || item;
          this.viewSelections = response.currentSelections || [];
        } else {
          this.showViewModal = false;
          this.$bvToast.toast("Failed to load poll responses.", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        }
      } catch (error) {
        console.error("Error loading poll responses:", error);
        this.showViewModal = false;
        this.$bvToast.toast("Failed to load poll responses.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.pollLoading = false;
        this.viewLoading = false;
      }
    },

    resetViewData() {
      this.viewPollData = null;
      this.viewSelections = [];
      this.viewLoading = false;
    },

    formatDate(dateString) {
      if (!dateString) return "—";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        return dateString;
      }
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadPolls();
    },

    onPageChange() {
      this.loadPolls();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";

::v-deep .planner-list-table {
  th.col-title,
  td.col-title {
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
    vertical-align: top;
    min-width: 180px;
    max-width: 320px;
  }
}

.table-title-text {
  display: block;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.45;
  font-weight: 600;
  color: #2d1b69;
}

.table-date-text {
  white-space: nowrap;
  font-size: 0.8125rem;
  color: #475569;
}

.leave-details-section {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.08);
  position: relative;

  &--items {
    overflow: visible;
  }

  &:not(.leave-details-section--items) {
    overflow: hidden;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #f0edff;
  }

  .section-icon {
    color: #7c5dfa;
    stroke-width: 2.5;
  }

  .section-title {
    font-size: 1.1rem;
    font-weight: 800;
    color: #2d1b69;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.details-meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.details-meta-item {
  background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  border: 1.5px solid #f0edff;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.06);

  &--wide {
    grid-column: 1 / -1;
  }

  .details-label {
    display: block;
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #8a86b7;
    letter-spacing: 1px;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .details-value {
    display: block;
    font-size: 1rem;
    font-weight: 700;
    color: #2d1b69;
    line-height: 1.3;

    &.details-value-primary {
      color: #7c5dfa;
    }

    &.details-value-info {
      color: #06b6d4;
    }

    &.details-value-success {
      color: #10b981;
    }
  }
}

.details-note-item {
  .details-label {
    display: block;
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #8a86b7;
    letter-spacing: 1px;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }
}

.details-note-content {
  background: #f8f9fa;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 0.85rem 1rem;
  font-size: 0.9rem;
  color: #4b4b4b;
  line-height: 1.55;
  white-space: pre-wrap;
}

.poll-option-row {
  gap: 0.5rem;
}

.poll-option-index {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(124, 93, 250, 0.12);
  color: #7c5dfa;
  font-weight: 700;
  font-size: 0.8125rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.poll-option-remove-btn {
  color: #dc2626;
  flex-shrink: 0;

  &:hover {
    color: #b91c1c;
  }
}

.poll-date-input {
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  min-height: 44px;
}

.poll-items-table-wrap {
  overflow-x: auto;
}

.poll-items-table {
  thead th {
    background: #f8f7ff !important;
    color: #2d1b69 !important;
    font-weight: 700;
    border-color: #e0d8ff !important;
    white-space: nowrap;
  }

  tbody td {
    vertical-align: middle;
    border-color: #f0edff !important;
  }
}

.poll-answer-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem;
  margin: 0.15rem 0.25rem 0.15rem 0;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
  font-size: 0.8125rem;
  font-weight: 600;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
